import axios from 'axios';
import { setToken, logout } from '../features/auth/authSlice';
import deviceService from '../services/deviceService';

const axiosInstance = axios.create({
  baseURL: 'https://server.survill.com',
  withCredentials: true,
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  
  failedQueue = [];
};

// Attach access token to every request
axiosInstance.interceptors.request.use((config) => {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Refresh on 401
export const setupInterceptors = (store) => {
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const originalRequest = error.config;

      if (error.response?.status === 401 && !originalRequest._retry) {
        // Don't retry refresh token requests
        if (originalRequest.url?.includes('/api/token/refresh/')) {
          store.dispatch(logout());
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // If we're already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          }).then(token => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return axiosInstance(originalRequest);
          }).catch(err => {
            return Promise.reject(err);
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
          processQueue(error, null);
          store.dispatch(logout());
          return Promise.reject(error);
        }

        try {
          // 🔑 UPDATED: Get device credentials for refresh (matches login behavior)
          let deviceCredentials = null;
          
          try {
            deviceCredentials = await deviceService.getDeviceCredentials();
            console.log('✓ Device credentials obtained for token refresh');
          } catch (deviceError) {
            console.warn('Device agent not available during refresh:', deviceError);
            // DON'T reject here - let backend decide if this is OK
            // Admins can refresh without device agent, non-admins cannot
          }

          // Prepare refresh payload
          const refreshPayload = {
            refresh: refreshToken,
          };

          // Include device credentials if available
          if (deviceCredentials) {
            refreshPayload.device_key = deviceCredentials.device_key;
            refreshPayload.mac_address = deviceCredentials.mac_address;
            refreshPayload.computer_name = deviceCredentials.computer_name;
            console.log('✓ Device credentials included in refresh request');
          } else {
            console.log('⚠ No device credentials - backend will check if user is admin');
          }

          // Create a new axios instance for refresh to avoid interceptor loops
          const refreshResponse = await axios.post(
            'https://server.survill.com/api/token/refresh/',
            refreshPayload,
            {
              headers: { 'Content-Type': 'application/json' },
            }
          );

          const { access } = refreshResponse.data;
          
          localStorage.setItem('accessToken', access);
          store.dispatch(setToken(access));
          
          // Update the original request
          originalRequest.headers.Authorization = `Bearer ${access}`;
          
          processQueue(null, access);
          
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
          processQueue(refreshError, null);
          store.dispatch(logout());
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      return Promise.reject(error);
    }
  );
};

export default axiosInstance;